import { useEffect, useState, type RefObject } from 'react';

/**
 * True once `ref`'s element has come within `rootMargin` of the viewport, and
 * true from then on (one-shot: it never flips back). Used to defer heavy
 * below-the-fold widgets until the reader is about to see them.
 *
 * Without IntersectionObserver (very old browsers, jsdom) it reports true at
 * once, so the content is never withheld.
 */
export function useNearViewport(
  ref: RefObject<Element | null>,
  rootMargin = '200px',
): boolean {
  const [near, setNear] = useState(false);

  useEffect(() => {
    if (near) return;
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [near, ref, rootMargin]);

  return near;
}
